import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { FaQuoteLeft } from 'react-icons/fa';
import { API } from '../config/api';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
  50% { box-shadow: 0 0 40px rgba(102, 126, 234, 0.6), 0 0 60px rgba(118, 75, 162, 0.4); }
`;

const AboutContainer = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
    opacity: 0.1;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: #ffffff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 300;
  letter-spacing: 1px;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const ProfileSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

const ProfileImage = styled.img`
  width: 300px;
  height: 300px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.2);
  animation: ${glow} 4s ease-in-out infinite;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const BioSection = styled(motion.div)`
  color: white;
`;

const BioTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #f093fb;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
`;

const Bio = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }
`;

const QuoteSection = styled(motion.div)`
  text-align: center;
  margin-top: 4rem;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Quote = styled.blockquote`
  font-size: 1.5rem;
  font-style: italic;
  color: white;
  margin-bottom: 1rem;
  font-weight: 300;
  line-height: 1.6;
  
  &::before, &::after {
    content: '"';
    font-size: 2rem;
    color: #f093fb;
    font-weight: 700;
  }
`;

const QuoteAuthor = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
`;

const About = () => {
  const [aboutData, setAboutData] = useState({
    profileImage: '',
    bio: '',
    quote: ''
  });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAboutData();
  }, []);
  
  const fetchAboutData = async () => {
    try {
      const response = await fetch(`${API}/about`);
      if (response.ok) {
        const data = await response.json();
        console.log('About data fetched:', data); // Debug log
        setAboutData(data);
      } else {
        console.error('Failed to fetch about data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching about data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Debug: Log current aboutData state
  console.log('Current aboutData state:', aboutData);
  
  return (
    <AboutContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <ContentWrapper>
        <Header
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Title>About Winter North</Title>
          <Subtitle>The Mind Behind the Stories</Subtitle>
        </Header>
        
        {loading ? (
          <div style={{ textAlign: 'center', color: 'white', fontSize: '1.2rem', padding: '4rem' }}>
            Loading...
          </div>
        ) : (
          <MainContent>
            <ProfileSection
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <ProfileImageWrapper>
                <ProfileImage
                  src={aboutData.profileImage ? `${API}${aboutData.profileImage}` : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face"}
                  alt="Winter North - Author"
                  onLoad={() => console.log('Image loaded:', aboutData.profileImage ? `${API}${aboutData.profileImage}` : 'fallback')}
                  onError={(e) => console.error('Image failed to load:', e.target.src)}
                />
              </ProfileImageWrapper>
            </ProfileSection>
            
            <BioSection
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <BioTitle>My Journey</BioTitle>
              <Bio>
                {aboutData.bio ? (
                  <div dangerouslySetInnerHTML={{ __html: aboutData.bio.replace(/\n/g, '</p><p>') }} />
                ) : (
                  <>
                    <p>
                      Welcome to my world of words and wonder. I'm Winter North, a passionate storyteller 
                      who believes that every story has the power to change lives. My journey began in the quiet 
                      corners of libraries, where I first discovered the magic that lies between the pages of a book.
                    </p>
                    <p>
                      Over the past decade, I've crafted narratives that explore the depths of human emotion, 
                      the complexities of relationships, and the extraordinary within the ordinary. My work spans 
                      multiple genres, from heart-wrenching literary fiction to thrilling mysteries that keep 
                      readers on the edge of their seats.
                    </p>
                    <p>
                      When I'm not writing, you'll find me traveling to remote locations for inspiration, 
                      studying human psychology, or mentoring aspiring writers. I believe that stories are 
                      the threads that weave us all together, creating a tapestry of shared human experience.
                    </p>
                  </>
                )}
              </Bio>
            </BioSection>
          </MainContent>
        )}
        
        <QuoteSection
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <Quote>
            {aboutData.quote || "Every story is a journey, and every journey changes both the traveler and those who follow along."}
          </Quote>
          <QuoteAuthor>— Winter North</QuoteAuthor>
        </QuoteSection>
      </ContentWrapper>
    </AboutContainer>
  );
};

export default About;
